import type { TaskManualTestsGroupDTO } from "@project/shared";
import {
  approveManualTestContract,
  editManualTestContract,
  listManualTestsContract,
  queryManualTestsContract,
  rejectManualTestContract,
  removeManualTestContract,
  setManualTestsContract,
} from "@project/shared/tool-contracts";
import type { AgentConnection } from "../connection/agent-connection.js";
import { defineContractTool } from "./contract-tool.js";
import { textResult } from "./helpers.js";

export function buildListManualTestsTool(connection: AgentConnection) {
  return defineContractTool(
    listManualTestsContract,
    async () => {
      try {
        const items = await connection.call("listManualTests", {
          sessionId: connection.sessionId,
        });
        if (items.length === 0) return textResult("No manual tests recorded for this task.");
        const lines = items.flatMap((item, i) => {
          const checked = item.checked ? "[x]" : "[ ]";
          const row = [`${i + 1}. ${checked} ${item.title}`];
          for (const f of item.failures ?? []) {
            const who = f.userName ?? "Someone";
            const reason = f.reason ?? "(no message)";
            row.push(`   ⚠ Failed (${who}): ${reason}`);
          }
          return row;
        });
        return textResult(lines.join("\n"));
      } catch {
        return textResult("Failed to list manual tests.");
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

/** Compact grouped rendering for cross-task manual-test queries: one header per
 * task, one line per test with a status glyph, and the failing reason indented
 * under each rejected test. */
function renderManualTestGroups(groups: TaskManualTestsGroupDTO[]): string {
  const lines: string[] = [];
  for (const g of groups) {
    lines.push(`## ${g.title} [${g.status}] (${g.slug})`);
    for (const t of g.tests) {
      const mark = t.status === "approved" ? "✓" : t.status === "rejected" ? "✗" : "○";
      lines.push(`  ${mark} ${t.title} — ${t.status}`);
      if (t.status === "rejected") {
        for (const f of t.failures ?? []) {
          lines.push(`      ⚠ ${f.userName ?? "Someone"}: ${f.reason ?? "(no message)"}`);
        }
      }
    }
    lines.push("");
  }
  return lines.join("\n").trimEnd();
}

export function buildQueryManualTestsTool(connection: AgentConnection) {
  return defineContractTool(
    queryManualTestsContract,
    async ({ cardStatuses, testStatuses }) => {
      try {
        const groups = await connection.call("queryManualTests", {
          sessionId: connection.sessionId,
          cardStatuses,
          testStatuses,
        });
        if (groups.length === 0) return textResult("No manual tests match those filters.");
        return textResult(renderManualTestGroups(groups));
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Unknown error";
        return textResult(`Failed to query manual tests: ${msg}`);
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

export function buildSetManualTestsTool(connection: AgentConnection) {
  return defineContractTool(setManualTestsContract, async ({ items }) => {
    try {
      const result = await connection.call("setManualTests", {
        sessionId: connection.sessionId,
        items,
      });
      const parts = [`Created ${result.created} manual test item(s).`];
      if (result.skipped > 0) parts.push(`Skipped ${result.skipped} duplicate(s).`);
      return textResult(parts.join(" "));
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Unknown error";
      return textResult(`Failed to set manual tests: ${msg}`);
    }
  });
}

export function buildEditManualTestTool(connection: AgentConnection) {
  return defineContractTool(editManualTestContract, async ({ title, newTitle }) => {
    try {
      await connection.call("editManualTest", {
        sessionId: connection.sessionId,
        title,
        newTitle,
      });
      return textResult(`Updated manual test to "${newTitle}".`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Unknown error";
      return textResult(`Failed to edit manual test: ${msg}`);
    }
  });
}

export function buildRemoveManualTestTool(connection: AgentConnection) {
  return defineContractTool(removeManualTestContract, async ({ title }) => {
    try {
      await connection.call("removeManualTest", {
        sessionId: connection.sessionId,
        title,
      });
      return textResult(`Removed manual test "${title}".`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Unknown error";
      return textResult(`Failed to remove manual test: ${msg}`);
    }
  });
}

export function buildApproveManualTestTool(connection: AgentConnection) {
  return defineContractTool(approveManualTestContract, async ({ title }) => {
    try {
      await connection.call("approveManualTest", {
        sessionId: connection.sessionId,
        title,
      });
      return textResult(`Approved manual test "${title}".`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Unknown error";
      return textResult(`Failed to approve manual test: ${msg}`);
    }
  });
}

export function buildRejectManualTestTool(connection: AgentConnection) {
  return defineContractTool(rejectManualTestContract, async ({ title, reason }) => {
    try {
      await connection.call("rejectManualTest", {
        sessionId: connection.sessionId,
        title,
        reason,
      });
      return textResult(`Flagged an issue with manual test "${title}": ${reason}`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Unknown error";
      return textResult(`Failed to reject manual test: ${msg}`);
    }
  });
}
